import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import Admin from './pages/Admin';
import Checkout from './pages/Checkout';
import EmailVerify from './pages/EmailVerify';
import Home from './pages/Home';
import Landing from './pages/Landing';
import OrderStatus from './pages/OrderStatus';
import GuideTicketDelivery from './pages/GuideTicketDelivery';
import MyTicket from './pages/MyTicket';
import Privacy from './pages/Privacy';
import StaffCheckin from './pages/StaffCheckin';
import StaffPos from './pages/StaffPos';
import Superadmin from './pages/Superadmin';
import './styles.css';

/*
 * A redirect that keeps the query string.
 *
 * `<Navigate to="/somewhere">` with a string path drops `?…` entirely, and every shortcut route
 * below is such a redirect. That went unnoticed while the query carried nothing — then the console
 * grew flags that live in it (`?nav=`, `?theme=`), and `/admin?nav=phases` started landing on
 * `/e/moonfield-fest/admin` with the flag gone. The page was correct, the switch never arrived,
 * and the only symptom was "I don't see any change", which points at the deploy rather than here.
 *
 * `/superadmin` never had the problem because it is a real route and not a redirect — which is
 * exactly why the two consoles behaved differently from the same URL bar.
 */
function Redirect({ to }: { to: string }) {
  const { search, hash } = useLocation();
  return <Navigate to={{ pathname: to, search, hash }} replace />;
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/checkout/:orderId" element={<Redirect to="/e/moonfield-fest" />} />
        <Route path="/order/:orderKey" element={<Redirect to="/e/moonfield-fest" />} />
        <Route path="/privacy" element={<Privacy />} />
        {/*
          TICK-093 — platform-level, not under `/e/:eventSlug`: a buyer's tickets span events, and
          nesting it under one would ask them to remember which event they are looking for before
          they can look for it.
        */}
        <Route path="/myticket" element={<MyTicket />} />
        {/* TICK-094 — organizer documentation, platform-level like the portal itself. */}
        <Route path="/guide/ticket-delivery" element={<GuideTicketDelivery />} />
        <Route path="/admin" element={<Redirect to="/e/moonfield-fest/admin" />} />
        <Route path="/staff/checkin" element={<Redirect to="/e/moonfield-fest/staff/checkin" />} />
        <Route path="/superadmin" element={<Superadmin />} />
        <Route path="/t/:token" element={<OrderStatus />} />
        {/* TICK-061: where an email Magic Link lands. Event-agnostic — the token identifies the order. */}
        <Route path="/verify/email/:token" element={<EmailVerify />} />

        {/* Any event provisioned from /superadmin, reachable by slug. */}
        <Route path="/e/:eventSlug" element={<Landing />} />
        <Route path="/e/:eventSlug/checkout/:orderId" element={<Checkout />} />
        <Route path="/e/:eventSlug/order/:orderKey" element={<OrderStatus />} />
        <Route path="/e/:eventSlug/admin" element={<Admin />} />
        <Route path="/e/:eventSlug/staff/checkin" element={<StaffCheckin />} />
        {/* TICK-078 — the counter. Its own role, because selling is not admitting. */}
        <Route path="/e/:eventSlug/staff/pos" element={<StaffPos />} />

        <Route path="*" element={<Redirect to="/e/moonfield-fest" />} />
      </Routes>
    </BrowserRouter>
  </React.StrictMode>,
);
